import React from 'react';

const ClientMetrics = ({ customerData, widgetSettings, isIBRole }) => {
  const currencySymbol = customerData?.preferred_currency === 'GBP' ? '£' : '$';

  // Get active metrics based on settings
  const getActiveMetrics = () => {
    const metrics = widgetSettings?.metrics || {};

    return Object.entries(metrics)
      .filter(([_, isActive]) => isActive !== false)
      .map(([metricId]) => metricId)
      .filter(Boolean);
  };


  // Get metric data for display
  const getMetricData = (metricId) => {
    const tooltips = widgetSettings?.tooltips || {};

    switch (metricId) {
      case 'aum':
        return {
          value: formatAUM(customerData?.total_aum),
          label: 'Total AUM',
          tooltip: tooltips.aum || "Assets Under Management (AUM) represents the total market value of investments that the client has entrusted to your firm to manage."
        };
      case 'ytdReturn':
        return {
          value: formatReturn(customerData?.ytd_return || "5.2"),
          label: 'YTD Return',
          tooltip: tooltips.ytdReturn || "Year-to-Date (YTD) Return shows the percentage gain or loss on the client's investments since the beginning of the current calendar year.",
          valueClass: parseFloat(customerData?.ytd_return) >= 0 ? 'text-success-dark' : 'text-error'
        };
      case 'timeHorizon':
        return {
          value: customerData?.time_horizon_category ? `${customerData.time_horizon_category}` : "Medium-term",
          label: 'Time Horizon',
          tooltip: tooltips.timeHorizon || "Time Horizon indicates the client's investment timeframe, which influences their investment strategy and risk tolerance."
        };
      case 'lastContact':
        return {
          value: customerData?.last_contact_date || "2026-01-10",
          label: 'Last Contact',
          tooltip: tooltips.lastContact || "Last Contact Date shows when you last communicated with this client, helping you maintain regular engagement."
        };
      case 'riskTolerance':
        return {
          value: customerData?.risk_tolerance || "Moderate",
          label: 'Risk Tolerance',
          tooltip: tooltips.riskTolerance || "The client's willingness to accept market volatility in exchange for potentially higher returns."
        };
      case 'investmentGoal':
        return {
          value: customerData?.investment_goal || "Retirement",
          label: 'Investment Goal',
          tooltip: tooltips.investmentGoal || "The primary financial objective the client is working towards."
        };
      case 'nextReview':
        return {
          value: customerData?.next_review_date || "2026-12-15",
          label: 'Next Review',
          tooltip: tooltips.nextReview || "The scheduled date for the next portfolio review with this client."
        };
      case 'clientSince':
        return {
          value: customerData?.client_since || "2018-03-22",
          label: 'Client Since',
          tooltip: tooltips.clientSince || "The date when this individual became your client."
        };
      // IB-specific metrics
      case 'proxyYtd': {
        const v = parseFloat(customerData?.annual_income);
        let fmtV = 'N/A';
        if (!isNaN(v)) {
          if (v >= 1e9) fmtV = `${currencySymbol}${(v / 1e9).toFixed(1)}B`;
          else if (v >= 1e6) fmtV = `${currencySymbol}${(v / 1e6).toFixed(1)}M`;
          else if (v >= 1e3) fmtV = `${currencySymbol}${(v / 1e3).toFixed(1)}K`;
          else fmtV = `${currencySymbol}${v.toFixed(0)}`;
        }
        return {
          value: fmtV,
          label: isIBRole ? 'Revenue YTD' : 'Proxy YTD',
          tooltip: tooltips.proxyYtd || "Year-to-date performance of the benchmark proxy most relevant to this institution's holdings."
        };
      }
      case 'pipelineWeight': {
        const pw = parseFloat(customerData?.pipeline_weight);
        let fmtPw = 'N/A';
        if (!isNaN(pw)) {
          if (pw >= 1e9) fmtPw = `${currencySymbol}${(pw / 1e9).toFixed(1)}B`;
          else if (pw >= 1e6) fmtPw = `${currencySymbol}${(pw / 1e6).toFixed(1)}M`;
          else if (pw >= 1e3) fmtPw = `${currencySymbol}${(pw / 1e3).toFixed(1)}K`;
          else fmtPw = `${currencySymbol}${pw.toFixed(0)}`;
        }
        return {
          value: fmtPw,
          label: 'Pipeline Weight',
          tooltip: tooltips.pipelineWeight || "Total value of live and prospective deals in the pipeline with this institution."
        };
      }
      case 'engagementScore': {
        const es = parseFloat(customerData?.engagement_score);
        return {
          value: isNaN(es) ? 'N/A' : `${es.toFixed(1)}`,
          label: 'Engagement Score',
          tooltip: tooltips.engagementScore || "Engagement score across recent touchpoints and interactions with this institution."
        };
      }
      case 'openAlerts': {
        const alerts = parseInt(customerData?.open_alerts, 10);
        return {
          value: isNaN(alerts) ? '0' : String(alerts),
          label: 'Open Alerts',
          tooltip: tooltips.openAlerts || "Number of open action items, compliance flags, or follow-up alerts for this institution.",
          valueClass: !isNaN(alerts) && alerts > 0 ? 'text-amber-500' : 'text-success-dark'
        };
      }
      default:
        return {
          value: "N/A",
          label: metricId,
          tooltip: "No description available"
        };
    }
  };

  const formatAUM = (aum) => {
    if (!aum) return `${currencySymbol}0`;

    if (aum >= 1000000000) {
      return `${currencySymbol}${(aum / 1000000000).toFixed(1)}B`;
    } else if (aum >= 1000000) {
      return `${currencySymbol}${(aum / 1000000).toFixed(1)}M`;
    } else if (aum >= 1000) {
      return `${currencySymbol}${(aum / 1000).toFixed(1)}K`;
    }
    return `${currencySymbol}${aum.toFixed(0)}`;
  };

  const formatReturn = (returnValue) => {
    if (!returnValue) return '0%';
    const value = parseFloat(returnValue);
    return value >= 0 ? `+${value}%` : `${value}%`;
  };

  const activeMetrics = getActiveMetrics();

  return (
    <div className={`grid gap-6 pt-5 border-t border-gray-100 ${activeMetrics.length > 4 ? 'grid-cols-5 lg:grid-cols-5 md:grid-cols-3 sm:grid-cols-2' : `grid-cols-${activeMetrics.length} lg:grid-cols-${activeMetrics.length} md:grid-cols-2 sm:grid-cols-2`} xs:grid-cols-1`}>
      {activeMetrics.map(metricId => {
        const metric = getMetricData(metricId);
        return (
          <div key={metricId} className="text-center relative group">
            <div className={`text-2xl font-bold mb-0.5 leading-tight cursor-help ${metric.valueClass || 'text-black'}`}
              style={{ color: metric.valueClass ? 'inherit' : (widgetSettings?.textColor || 'inherit') }}>
              {metric.value}
              <div className="invisible group-hover:visible opacity-0 group-hover:opacity-100 absolute bottom-[125%] left-1/2 -translate-x-1/2 bg-white text-gray-600 py-1.5 px-2.5 rounded text-sm w-[200px] z-[100] shadow-sm text-center pointer-events-none border border-gray-200 transition-opacity duration-200">
                {metric.tooltip}
                <div className="absolute top-full left-1/2 -ml-1 border-4 border-transparent border-t-gray-200"></div>
              </div>
            </div>
            <div className="text-sm text-gray-500" style={{ color: widgetSettings?.textColor ? `${widgetSettings.textColor}99` : 'inherit' }}>
              {metric.label}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ClientMetrics;